import type { ReactNode } from 'react'
import type { Topic } from '@/api/topics/types'
import { LineChart, type LinePoint } from '@/components/charts/LineChart'
import { formatCurrency, formatDecimal, formatMonth, formatNumber } from '@/lib/format'

interface TopicDetailProps {
  readonly topic: Topic
}

/**
 * Der Saisonverlauf eines einzelnen Themas.
 *
 * Die Monatswerte kommen bereits sortiert aus dem Backend, trotzdem wird
 * hier nochmal sortiert: Auf die Reihenfolge einer fremden Antwort zu
 * bauen, bringt bei einem Liniendiagramm einen Zickzack statt eines
 * Fehlers, und der fällt niemandem auf.
 */
function toLinePoints(topic: Topic): readonly LinePoint[] {
  return [...(topic.monthlyVolumes ?? [])]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((entry) => ({
      label: formatMonth(entry.year, entry.month),
      value: entry.searchVolume,
    }))
}

function Metric({
  label,
  value,
  context,
}: {
  readonly label: string
  readonly value: string
  readonly context?: string
}): ReactNode {
  return (
    <div>
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {value}
        {context !== undefined && (
          <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
            {context}
          </span>
        )}
      </dd>
    </div>
  )
}

export function TopicDetail({ topic }: TopicDetailProps): ReactNode {
  const points = toLinePoints(topic)

  return (
    <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-700">
      {topic.ranking ? (
        <dl className="mb-4 grid grid-cols-2 gap-4 rounded bg-slate-50 p-3 sm:grid-cols-4 dark:bg-slate-900">
          <Metric label="Ranking-Platz" value={`#${String(topic.ranking.position)}`} />
          <Metric label="Score" value={formatDecimal(topic.ranking.score)} />
          <Metric
            label="Marge je Stück"
            value={formatCurrency(topic.ranking.marginPerUnit)}
            {...(topic.marginGroup ? { context: topic.marginGroup.label } : {})}
          />
          <Metric
            label="Volumen im Ranking"
            value={formatNumber(topic.ranking.searchVolume)}
            context="/ Monat"
          />
        </dl>
      ) : (
        <p className="mb-4 rounded bg-slate-50 px-3 py-2 text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          Für dieses Thema wurde noch kein Ranking gerechnet.
        </p>
      )}

      <dl className="mb-4 grid grid-cols-3 gap-4">
        <Metric label="CPC" value={formatCurrency(topic.cpc)} />
        <Metric label="Wettbewerb" value={formatDecimal(topic.cmp)} />
        <Metric label="Trend" value={formatDecimal(topic.trend)} />
      </dl>
      <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
        CPC, Wettbewerb und Trend gehören zum stärksten Keyword des Themas, nicht zum Thema als
        Ganzes.
      </p>

      {points.length > 1 && (
        <div className="mb-4">
          <h4 className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">
            Suchvolumen im Verlauf
          </h4>
          <LineChart data={points} seriesName={topic.topic} height={180} />
        </div>
      )}

      <h4 className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">
        Keywords ({formatNumber(topic.keywordCount)})
      </h4>
      <ul className="divide-y divide-slate-100 dark:divide-slate-700">
        {topic.keywords.map((keyword) => (
          <li
            key={keyword.keywordId}
            className="flex items-center justify-between gap-4 py-1.5 text-sm"
          >
            <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">
              {keyword.value}
              {keyword.subtype !== null && keyword.subtype !== '' && (
                <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">
                  {keyword.subtype}
                </span>
              )}
            </span>
            <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
              {formatNumber(keyword.searchVolumePerMonthAvg)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
